import { ChurnZeroEvents } from "./events";
import { events } from "../../../framework";
import _ from "lodash";
import { makeAPIClient } from "../../core/util/util";
import { analyticsInfoEndpoint } from "../analytics";
import * as z from "../../../framework";

const requiredFields = {
    "AppKey": "AppKey",
    "AccountExternalId": "AccountExternalId",
    "ContactExternalId": "ContactExternalId",
    "AccountExternalIdHash": "AccountExternalIdHash",
    "ContactExternalIdHash": "ContactExternalIdHash"
} as const;

type RequiredField = keyof typeof requiredFields;
type ChurnZeroConfig = {
    [key in RequiredField]: string;
}

export const churnZeroConfigKey = "ChurnZeroConfig";

export function injectChurnZero() {
    (async function () {
        var cz = document.createElement('script'); cz.type = 'text/javascript';
        cz.async = true;
        cz.src = 'https://analytics.churnzero.net/churnzero.js';
        var s = document.getElementsByTagName('script')[0]; s.parentNode.insertBefore(cz, s);
    })();
    events.register(events.pageDoneLoading, initializeChurnZero);
}

export let churnZeroInitialized = false;

async function initializeChurnZero() {
    try {
        const enableMSIChurnZeroEvents = z.util.getBooleanOption("EnableMSIChurnZeroEvents", false);
        const worksAtMsi = z.user.loginData.UserName.endsWith("@msidata.com");

        const shouldInitializeChurnZero = !worksAtMsi || (enableMSIChurnZeroEvents && worksAtMsi);
        if (shouldInitializeChurnZero) {
            const config = await getChurnZeroConfig();
            validateChurnZeroConfig(config);
            applyChurnZeroConfig(config);
        }
    } catch (error) {
        console.warn("Could not initialize ChurnZero.", error);
        stopChurnZero();
    }
}

async function getChurnZeroConfig(): Promise<ChurnZeroConfig> {

    const cachedConfig = getCachedConfig();

    if (cachedConfig) {
        return Promise.resolve(cachedConfig);
    } else {
        return fetchChurnZeroConfig();
    }
}

function getCachedConfig(): ChurnZeroConfig {
    const config = JSON.parse(window.z.getLocalStorage(churnZeroConfigKey) || "{}");
    if (config && !(_.isEmpty(config))) {
        return config;
    }
    return null;
}

function cacheConfig(config: ChurnZeroConfig) {
    window.z.setLocalStorage(churnZeroConfigKey, JSON.stringify(config));
}

async function fetchChurnZeroConfig(): Promise<ChurnZeroConfig> {
    const apiClient = makeAPIClient({serviceProApiUrl: window.z.configuration.serviceProApiUrl || ""});
    const churnZeroConfig = apiClient.get(analyticsInfoEndpoint)
        .then(response => {
            const config: ChurnZeroConfig = JSON.parse(response.data.d.results).ChurnZero;
            cacheConfig(config);
            return config;
        });
    return churnZeroConfig;
}

function validateChurnZeroConfig(config: ChurnZeroConfig) {
    _.forEach(requiredFields,
            field => {
            if (!config[field]) {
                throw new Error(`Incomplete ChurnZero configuration. Need ${Object.keys(requiredFields).join(", ")}`);
            }
        });
}

function applyChurnZeroConfig(churnZeroConfig: ChurnZeroConfig) {
    const { AppKey, AccountExternalId, ContactExternalId, AccountExternalIdHash, ContactExternalIdHash } = churnZeroConfig;

    window.ChurnZero.push(['setAppKey', AppKey]);

    window.ChurnZero.push(['setAuth', () => ({
        "Authorization": `${z.user.loginData.SelectedCompanyId} ${z.user.loginData.UserName.toLowerCase()}`
    })]);
    window.ChurnZero.push(['setContact', AccountExternalId, ContactExternalId, AccountExternalIdHash, ContactExternalIdHash]);
    completeInitialization();
}

function completeInitialization() {
    churnZeroInitialized = true;
    events.fire(ChurnZeroEvents.churnZeroInitialized);
}

export function stopChurnZero() {
    window.ChurnZero?.push(['stop']);
    churnZeroInitialized = false;
    events.fire(ChurnZeroEvents.churnZeroStopped);
}